import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api, { saveAuth, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import PricingPreview from "@/components/PricingPreview";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const initMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [structureName, setStructureName] = useState("");
  const [cin, setCin] = useState("");
  const [loading, setLoading] = useState(false);
  const { t, lang } = useLang();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    // Client-side CIN validation (register only)
    if (mode === "register") {
      const clean = cin.trim().toUpperCase().replace(/[\s-]/g, "");
      if (!/^IT[A-Z0-9]{14,20}$/.test(clean)) {
        toast.error(lang === "it"
          ? "Codice CIN non valido. Deve iniziare con 'IT' seguito da 14-20 caratteri alfanumerici."
          : "Invalid CIN code. Must start with 'IT' followed by 14-20 alphanumeric characters.");
        return;
      }
    }
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email, password }
        : { email, password, structure_name: structureName, cin: cin.trim().toUpperCase().replace(/[\s-]/g, "") };
      const r = await api.post(path, body);
      saveAuth(r.data.token, r.data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      nav("/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-[420px_1fr] gap-10 items-start">
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">Dashboard host</div>
          <h1 className="font-display text-4xl tracking-tight mb-2" data-testid="auth-title">
            {mode === "login" ? t("auth.login") : t("auth.register")}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">{t("auth.demo_hint")}</p>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <Label htmlFor="structure">{t("auth.structure")}</Label>
              <Input id="structure" data-testid="input-structure" value={structureName} onChange={(e)=>setStructureName(e.target.value)} required />
            </div>
          )}
          {mode === "register" && (
            <div>
              <Label htmlFor="cin" className="flex items-center gap-1">
                {t("auth.cin_label")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cin"
                data-testid="input-cin"
                value={cin}
                onChange={(e)=>setCin(e.target.value.toUpperCase())}
                placeholder="IT058091C25010A5B"
                maxLength={25}
                required
                pattern="^IT[A-Za-z0-9 \-]{13,25}$"
                title={lang === "it" ? "Inizia con IT + 14-20 caratteri (16-22 totali, es. Sicilia 18)" : "Starts with IT + 14-20 chars (16-22 total, e.g. Sicily 18)"}
                className="uppercase font-mono tracking-wider"
              />
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {t("auth.cin_help")}
              </p>
            </div>
          )}
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" data-testid="input-email" value={email} onChange={(e)=>setEmail(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input id="password" type="password" data-testid="input-password" value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={6}/>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="auth-submit">
            {mode === "login" ? t("auth.submit_login") : t("auth.submit_register")}
          </Button>
        </form>

        {mode === "login" && (
          <Link
            to="/forgot-password"
            className="mt-4 text-sm text-muted-foreground hover:text-primary transition-colors block text-center"
            data-testid="auth-forgot-link"
          >
            {t("auth.forgot")}
          </Link>
        )}

        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-6 text-sm text-muted-foreground hover:text-primary transition-colors block mx-auto"
          data-testid="auth-switch"
        >
          {mode === "login" ? t("auth.switch_to_register") : t("auth.switch_to_login")}
        </button>
        </div>

        {/* Pricing preview for hosts */}
        <div className="lg:sticky lg:top-6">
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2">
            {lang === "it" ? "Piani per host" : "Host plans"}
          </div>
          <h2 className="font-display text-2xl mb-4">
            {lang === "it" ? "Scegli il piano che ti serve" : "Pick the plan that fits"}
          </h2>
          <PricingPreview compact={false} showLink={true}/>
        </div>
      </div>
    </div>
  );
}
